/**
 * Page: SeasonalMenuCreate
 * Author: Dine AI
 * Last Modified: December 17, 2025 - Light mode conversion
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Calendar, 
  FileText, 
  Upload, 
  Copy,
  Snowflake,
  Sun,
  Leaf,
  Flower2
} from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';
import { useToast } from '@/components/ui/use-toast';

const SeasonalMenuCreate = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [menuName, setMenuName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!menuName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a menu name",
        variant: "destructive"
      });
      return;
    }

    try {
      setCreating(true);
      const response = await apiClient.post('/seasonal-menus', {
        menu_name: menuName,
        description: description || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined
      });

      toast({
        title: "Success",
        description: "Special menu created successfully",
      });

      // Navigate to restaurant menu upload with seasonal_menu_id
      navigate(`/restaurant-menu-upload?seasonal_menu_id=${response.data.seasonal_menu_id}`);
    } catch (error: any) {
      console.error('Error creating seasonal menu:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || 'Failed to create special menu',
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  const getSeasonalIcon = () => {
    const name = menuName.toLowerCase();
    if (name.includes('winter') || name.includes('holiday')) return <Snowflake className="h-8 w-8" />;
    if (name.includes('summer')) return <Sun className="h-8 w-8" />;
    if (name.includes('fall') || name.includes('autumn')) return <Leaf className="h-8 w-8" />;
    if (name.includes('spring')) return <Flower2 className="h-8 w-8" />;
    return <Calendar className="h-8 w-8" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Light Mode */}
      <div className="border-b border-gray-200 bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/restaurant-dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Create Special Menu</h1>
          <p className="text-gray-500 text-lg">
            Create special menus for holidays, brunch, happy hour, events, or limited-time promotions
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Menu Name */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Menu Name *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={menuName}
                  onChange={(e) => setMenuName(e.target.value)}
                  placeholder="e.g., Summer BBQ Special, Holiday Feast, Brunch Menu, Happy Hour"
                  className="w-full px-4 py-3 pl-12 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]/20 transition-all"
                  required
                />
                <div className="absolute left-3 top-3 text-gray-400">
                  {getSeasonalIcon()}
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your special menu..."
                rows={3}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]/20 transition-all resize-none"
              />
            </div>

            {/* Date Range */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]/20 transition-all"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913]/20 transition-all"
                />
              </div>
            </div>

            {/* Next Steps Preview */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h3 className="text-gray-900 font-medium mb-4">Next Steps</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-[#FDB913]/10 rounded-lg">
                    <Upload className="h-5 w-5 text-[#FDB913]" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">Upload Menu PDF</p>
                    <p className="text-gray-500 text-sm">Upload a PDF of your special menu</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">Add Items Manually</p>
                    <p className="text-gray-500 text-sm">Create items one by one</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Copy className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium">Copy from Main Menu</p>
                    <p className="text-gray-500 text-sm">Select items from your existing menu</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/restaurant-dashboard')}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !menuName.trim()}
                className="flex-1 px-6 py-3 bg-[#FDB913] text-black rounded-lg font-medium hover:bg-[#e5a711] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Calendar className="h-5 w-5" />
                    Create Special Menu
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default SeasonalMenuCreate;